import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Mail } from 'lucide-react';
import { SeoHead } from '../components/SeoHead';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const faqs = [
  {
    category: 'General',
    items: [
      {
        q: 'What is Rooam Sports?',
        a: 'Rooam Sports Network is the all-in-one platform for live youth and amateur sports. We provide HD live streaming, real-time stats and box scores, athlete profiles, full game replays, and recruiting tools — all in one place.',
      },
      {
        q: 'Who is Rooam for?',
        a: 'Rooam is built for athletes, parents, coaches, scouts, and sports fans. Whether you want to watch a game live, track player stats, or get exposure for recruiting, Rooam has you covered.',
      },
      {
        q: 'What sports does Rooam support?',
        a: 'We currently focus on basketball, soccer, volleyball, and football. Other sports are in the works.',
      },
    ],
  },
  {
    category: 'Streaming & Viewing',
    items: [
      {
        q: 'How do I watch a live game?',
        a: 'Head to the Events page, find the event or game you want to watch, and click on it. If the game is live, you\'ll see the stream along with real-time stats and box scores.',
      },
      {
        q: 'Can I watch games for free?',
        a: 'You can preview any live game for 60 seconds. After that, you\'ll need to create an account and subscribe to a membership plan to continue watching.',
      },
      {
        q: 'Can I watch replays of past games?',
        a: 'Yes! Games that were streamed through Rooam are automatically saved as replays. You can find them on the game page after the event ends.',
      },
    ],
  },
  {
    category: 'Membership',
    items: [
      {
        q: 'What membership plans are available?',
        a: 'We offer a Fan Pass for full streaming and replay access, and an All-Access plan that includes advanced stats, player profiles, and priority support. Visit our Membership page for current pricing.',
      },
      {
        q: 'Can I cancel my membership?',
        a: 'Yes, you can cancel anytime. Your access continues until the end of your current billing period.',
      },
    ],
  },
  {
    category: 'Athletes & Coaches',
    items: [
      {
        q: 'How do I create an athlete profile?',
        a: 'Register for an account, then go to the Athlete Directory and click "Create Profile." You can add your stats, highlight reels, and connect your profile to game rosters.',
      },
      {
        q: 'How do coaches claim a team?',
        a: 'Coaches can register with a coach account, find their team on the Teams page, and claim it. Once claimed, you can manage rosters and team information.',
      },
      {
        q: 'How does recruiting exposure work?',
        a: 'When athletes are linked to game rosters, their stats are automatically tracked and displayed on their profiles. Scouts and recruiters can browse athlete profiles and watch their game footage.',
      },
    ],
  },
  {
    category: 'Tournaments & Events',
    items: [
      {
        q: 'How do I get my tournament on Rooam?',
        a: 'Visit our Partner page and fill out the partnership form. We work with tournament directors, leagues, and schools to bring their events to the platform.',
      },
      {
        q: 'Can I stream my own games?',
        a: 'Yes! Contact us through the Partner page to get set up.',
      },
    ],
  },
];

export default function FAQ() {
  return (
    <div className="min-h-screen bg-rooam-black" data-testid="faq-page">
      <SeoHead
        title="FAQ — Frequently Asked Questions"
        description="Answers to common questions about Rooam Sports Network — how live streaming works, what subscriptions include, how creators earn 70% of ad revenue, and how athletes claim profiles."
        path="/faq"
      />
      <div className="max-w-3xl mx-auto px-4 py-12">
        <Link to="/" className="inline-flex items-center gap-1 text-gray-400 hover:text-white text-sm mb-8">
          <ChevronLeft className="h-4 w-4" /> Back
        </Link>

        <h1 className="text-3xl sm:text-4xl font-black text-white mb-3" data-testid="faq-title">
          Frequently Asked Questions
        </h1>
        <p className="text-gray-400 mb-10">
          Everything you need to know about Rooam Sports Network.
        </p>

        <div className="space-y-8">
          {faqs.map((section) => (
            <div key={section.category}>
              <h2 className="text-lg font-bold text-rooam-blue mb-3" data-testid={`faq-category-${section.category.toLowerCase().replace(/\s+/g, '-')}`}>
                {section.category}
              </h2>
              <Accordion type="single" collapsible className="space-y-2">
                {section.items.map((item, i) => (
                  <AccordionItem
                    key={i}
                    value={`${section.category}-${i}`}
                    className="bg-rooam-gray-medium border border-rooam-gray-light rounded-lg px-4 data-[state=open]:border-rooam-blue/30"
                  >
                    <AccordionTrigger className="text-sm text-white font-medium hover:no-underline py-3" data-testid={`faq-q-${section.category.toLowerCase().replace(/\s+/g, '-')}-${i}`}>
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-gray-400 leading-relaxed pb-4">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-rooam-gray-medium border border-rooam-gray-light rounded-xl p-6 text-center">
          <p className="text-white font-semibold mb-2">Still have questions?</p>
          <p className="text-gray-400 text-sm mb-4">We're here to help.</p>
          <a
            href="mailto:support@rooamsports.network"
            className="inline-flex items-center gap-2 bg-rooam-blue hover:bg-rooam-blue-light text-black font-bold text-sm px-5 py-2.5 rounded-lg transition-colors"
            data-testid="faq-contact-btn"
          >
            <Mail className="h-4 w-4" /> Contact Support
          </a>
        </div>
      </div>
    </div>
  );
}
